/**
 * ----------------------------------------------------------------------------------------------------------------------
 * COMPACT QUICK BUY
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Adds the product card's media arrows to the gallery of the trimmed quick buy panel, opened from a product hot spot
 * (blocks/_hot-spot-content-product.liquid) or a product card (snippets/product-card.liquid). Loaded once for every page
 * in layout/theme.liquid.
 *
 * The panel clones the markup rendered by snippets/product-quick-buy.liquid, which asks the gallery for its "lines"
 * controls. That snippet is shared with every other quick buy in the theme, so the controls cannot be swapped for one
 * caller in Liquid — the arrows are added here instead, and "quick-buy-compact.css" hides the lines on desktop.
 *
 * The buttons themselves are the theme's own <carousel-prev-button> and <carousel-next-button>: they take the id of the
 * carousel they drive, move it on click, disable themselves at either end and hide themselves when there is nothing to
 * scroll. Only the markup is ours.
 *
 * It also sends the "notify me" button of a sold out size to the product page, with that size selected.
 */

(() => {
  const MODAL_SELECTOR = 'quick-buy-modal.modal--quick-buy-compact';

  // Same paths as the "arrow-left" and "arrow-right" icons in snippets/icon.liquid.
  const ICONS = {
    previous:
      '<svg aria-hidden="true" focusable="false" width="12" viewBox="-1 -1 12 12" fill="none"><path stroke="currentColor" stroke-width="1.5" d="M6 1 2 5m0 0 4 4M2 5h8"/></svg>',
    next: '<svg aria-hidden="true" focusable="false" width="12" viewBox="-1 -1 12 12" fill="none"><path stroke="currentColor" stroke-width="1.5" d="m4 1 4 4m0 0L4 9m4-4H0"/></svg>',
  };

  const createButton = (tagName, carouselId, label, icon) => {
    const element = document.createElement(tagName);

    element.className = 'quick-buy__media-arrow';
    element.setAttribute('aria-controls', carouselId);
    element.innerHTML = `<button type="button" aria-label="${label}">${icon}</button>`;

    return element;
  };

  const addMediaNav = (modal) => {
    const imageList = modal.querySelector('.product-gallery__image-list');
    const carousel = imageList?.querySelector('scroll-carousel[id]');

    if (!carousel || imageList.querySelector('.quick-buy__media-nav')) {
      return;
    }

    // A single image has nothing to navigate. Media hidden by the variant filter does not count.
    if (carousel.querySelectorAll('.product-gallery__media:not([hidden])').length < 2) {
      return;
    }

    const nav = document.createElement('div');
    nav.className = 'quick-buy__media-nav';

    nav.append(
      createButton('carousel-prev-button', carousel.id, modal.dataset.previousLabel ?? '', ICONS.previous),
      createButton('carousel-next-button', carousel.id, modal.dataset.nextLabel ?? '', ICONS.next)
    );

    imageList.append(nav);
  };

  // Picking a size re-renders the panel: <product-rerender> replaces itself, a direct child of the modal, which drops
  // the arrows with the old gallery.
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length > 0) {
        addMediaNav(mutation.target);
      }
    });
  });

  // Product cards come and go after the page has loaded (filters, pagination, recommendations, the theme editor), so
  // modals are picked up when they open rather than when the page loads. The modal fills itself with the product page
  // before "dialog:before-show", and that event does not bubble: it is caught on its way down, in the capture phase.
  document.addEventListener(
    'dialog:before-show',
    (event) => {
      const modal = event.target;

      if (!modal.matches?.(MODAL_SELECTOR)) {
        return;
      }

      if (!modal.quickBuyCompactWatched) {
        modal.quickBuyCompactWatched = true;
        observer.observe(modal, { childList: true });
      }

      addMediaNav(modal);
    },
    { capture: true }
  );

  // For a sold out variant, snippets/button.liquid gives the button content the "klaviyo-bis-trigger" class, and
  // Klaviyo's onsite script opens its back in stock form on click. From inside the panel the customer is sent to the
  // product page instead, with the size they picked. The variant picker keeps that size in the form's "id" input.
  // Listening on window in the capture phase runs before Klaviyo's handler and before the button submits the form, and
  // it survives the panel being re-rendered on every size change. The button itself is matched too: a keyboard press
  // clicks the button, not its content.
  window.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('.klaviyo-bis-trigger, button:has(> .klaviyo-bis-trigger)');
      const modal = trigger?.closest(MODAL_SELECTOR);

      if (!modal) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const url = new URL(modal.getAttribute('product-url'), window.location.origin);
      const variantId = trigger.closest('form')?.elements.id?.value;

      if (variantId) {
        url.searchParams.set('variant', variantId);
      }

      window.location.assign(url);
    },
    { capture: true }
  );
})();
